import type { FC } from "react"
import React, { useState } from "react"
import type { FlexProps } from "@chakra-ui/react"
import {
  Skeleton,
  TabPanels,
  Tab,
  TabList,
  Tabs,
  Flex,
  TabPanel,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import { useDevice } from "common/hooks/useDevice"
import type { Maybe, VoteStats } from "query/graphql"
import { ProposalStatus, useProposalVotesCastQuery } from "query/graphql"
import ProposalBubbleChart from "proposal/components/ProposalBubbleChart"
import ProposalTimelineChart from "proposal/components/ProposalTimelineChart"
import VotesCastResults, {
  VotesCastResultStatus,
} from "common/components/voting/VotesCastResults"
import CardContainer from "common/components/CardContainer"
import { ROUTES } from "common/constants/routes"
import VotesCast from "common/components/voting/VotesCast"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useProposal } from "proposal/providers/ProposalProvider"
import { useVotesCastResultsCalculations } from "common/hooks/useVotesCastResultsCalculations"
import ProposalNotYetVoted from "proposal/components/ProposalNotYetVoted"
import { WORMHOLE_SLUGS } from "governance/components/wormhole/constants"

type ChartCode = "bubble" | "timeline" | "voted" | "not-yet-voted"

const CHARTS: ChartCode[] = ["bubble", "timeline", "voted", "not-yet-voted"]

type Props = {
  proposalId: string
  onchainId?: Maybe<string>
  slug: string
}

const ProposalCharts: FC<Props & FlexProps> = ({
  proposalId,
  onchainId,
  slug,
  ...flexProps
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const { organization } = useOrganization()
  const { isIncludePendingVotes } = useProposal()

  // Create an object with each ChartCode and a boolean (false) to then
  // be used to save if the chart was rendered or not
  const chartRenderedInit: { [key: string]: boolean } = CHARTS.reduce(
    (acc, chart) => {
      acc[chart as string] = false

      return acc
    },
    {} as { [key: string]: boolean }, // Add index signature to the empty object
  )

  const [chartCode, setChartCode] = useState<ChartCode>("bubble")

  const [chartRendered, setChartRendered] = useState(chartRenderedInit)

  const handleTabChange = (index: number) => {
    const chartCode = CHARTS[index]
    setChartCode(chartCode)
    setChartRendered({
      ...chartRendered,
      [chartCode]: true,
    })
  }

  const { data, isLoading } = useProposalVotesCastQuery({
    input: {
      id: proposalId,
    },
  })

  const { isQuorumMet, totalWeightLabel, quorumLabel, isMajoritySupport } =
    useVotesCastResultsCalculations({
      quorum: data?.proposal.quorum ?? "0",
      decimals: data?.proposal.governor?.token.decimals ?? 18,
      voteStats: data?.proposal.voteStats,
      isIncludePendingVotes,
      governorType: data?.proposal?.governor?.type,
    })

  if (isLoading) {
    return (
      <CardContainer isTableView title="Votes">
        <Skeleton as="section" h="30.0875rem" mt={4} mx={2} />
      </CardContainer>
    )
  }

  if (!data || !data?.proposal) return null

  const { proposal } = data
  const { governor, voteStats } = proposal

  invariant(governor, "governor is required")

  const { token, id: govId } = governor
  const { decimals } = token
  const isProposalActive =
    proposal.status === ProposalStatus.Active ||
    proposal.status === ProposalStatus.Extended
  const isProposalPending = proposal.status === ProposalStatus.Pending

  return (
    <>
      <Flex
        as="section"
        direction="column"
        display={onLargeDevice}
        {...flexProps}
        maxW="100%"
        mt={0}
        w="800px"
      >
        <Tabs colorScheme="purple" defaultIndex={0} onChange={handleTabChange}>
          <CardContainer isTableView pb={0} title="Result details">
            <TabList>
              <Tab pl={5}>Bubble</Tab>
              <Tab>Timeline</Tab>
              <Tab>Voted</Tab>
              {isProposalActive &&
              organization &&
              !WORMHOLE_SLUGS.includes(organization.slug) ? (
                <Tab>Not yet voted</Tab>
              ) : null}
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <ProposalBubbleChart
                  displayChart={
                    chartCode === "bubble" || chartRendered["bubble"]
                  }
                  proposalId={proposalId}
                  slug={slug}
                />
              </TabPanel>
              <TabPanel p={0}>
                <ProposalTimelineChart
                  displayChart={
                    chartCode === "timeline" || chartRendered["timeline"]
                  }
                  proposalId={proposalId}
                />
              </TabPanel>
              <TabPanel p={0}>
                <VotesCast
                  decimals={decimals}
                  displayChart={chartCode === "voted" || chartRendered["voted"]}
                  generateVoterLink={(address) =>
                    ROUTES.governance.delegate.profile(slug, address)
                  }
                  proposalId={proposalId}
                  viewAllVotesLink={ROUTES.governance.proposal.votes(
                    slug,
                    onchainId ?? proposalId,
                    organization && organization.governorIds?.length > 1
                      ? govId
                      : "",
                  )}
                  voteStats={voteStats as VoteStats[]}
                />
              </TabPanel>
              {isProposalActive &&
              organization &&
              !WORMHOLE_SLUGS.includes(organization.slug) ? (
                <TabPanel p={0}>
                  <ProposalNotYetVoted proposalId={proposalId} />
                </TabPanel>
              ) : null}
            </TabPanels>
          </CardContainer>
        </Tabs>
      </Flex>
      <Flex
        as="section"
        direction="column"
        display={onLittleDevice}
        mt={0}
        pt={0}
        w="full"
        {...flexProps}
      >
        {!isProposalPending ? (
          <CardContainer
            isTableView
            pb={2}
            title={isProposalActive ? "Current Votes" : "Final Votes"}
          >
            <VotesCastResults
              decimals={decimals}
              isIncludePendingVotes={isIncludePendingVotes}
              voteStats={voteStats as VoteStats[]}
              votesCastResultStatuses={
                <>
                  <VotesCastResultStatus
                    isSuccess={isQuorumMet}
                    label="Quorum"
                    value={`${totalWeightLabel} of ${quorumLabel}`}
                  />
                  <VotesCastResultStatus
                    isSuccess={isMajoritySupport}
                    label="Majority support"
                    value={isMajoritySupport ? "Yes" : "No"}
                  />
                </>
              }
            />
          </CardContainer>
        ) : null}
      </Flex>
    </>
  )
}

export default ProposalCharts
